"use client";

import { useEffect } from "react";
import { useReportWebVitals } from "next/web-vitals";

import { telemetryUrl } from "@/lib/telemetry-url";

/**
 * Phase 0 (perf/lighthouse-100): real-user monitoring for Core Web Vitals.
 * Logs LCP / INP / CLS / FCP / TTFB to the console. In production the same
 * lines are emitted as structured JSON so log-scrapers can lift them into
 * RUM dashboards without a separate analytics endpoint.
 *
 * Phase 3B.4 (this commit): also surfaces
 *   - the document `load` event timing (i.e. total page load from
 *     `performance.timing` — closest browser-native analogue to "how long
 *     did the page take to fully load"), AND
 *   - server-side render timings the page embeds as a JSON data island
 *     (`<script id="__tc_page_timings__">`) — the agency, experience, and
 *     availability API durations from `apiFetch`/`getCachedExperience`
 *     calls inside the RSC render. Lets the operator see per-call
 *     timings in DevTools console without tailing server logs.
 *
 * Mounted in the root layout. Adds < 1 KB to the client bundle.
 */

interface PageTimings {
  agency: number;
  experience: number;
  availability: number;
  render: number;
  slug: string;
}

// `process.env.NODE_ENV` is statically inlined by Next at build time, so this
// avoids importing `@/env.client` (which pulls all of zod into the shared
// client chunk on every route — see instrumentation-client.ts).
// eslint-disable-next-line no-restricted-properties -- NODE_ENV is statically inlined by Next
const isDev = process.env.NODE_ENV !== "production";

// Beacon endpoint. /api/rum is an edge route handler that logs one JSON line
// per call so platform-log scrapers can lift records into a RUM dashboard.
// `sendBeacon` is fire-and-forget and survives page unload — the right
// primitive for vitals like LCP/INP that may not arrive until late.
const RUM_ENDPOINT = "/api/rum";

function ship(body: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const payload = JSON.stringify({
    ...body,
    // Redacted, not raw. `/claim/<token>` carries a bearer credential in the
    // path, and this payload is written to a log line by /api/rum. See
    // lib/telemetry-url.ts.
    url: telemetryUrl(window.location.pathname, window.location.search),
  });
  // sendBeacon path: queued by the browser, immune to navigation aborts.
  if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      const queued = navigator.sendBeacon(RUM_ENDPOINT, blob);
      if (queued) return;
    } catch {
      // fall through to fetch
    }
  }
  // Fetch fallback (older Safari, Firefox extensions that disable beacon).
  // keepalive lets the request outlive the document for short payloads.
  void fetch(RUM_ENDPOINT, {
    method: "POST",
    body: payload,
    headers: { "content-type": "application/json" },
    keepalive: true,
  }).catch(() => {});
}

function emit(name: string, payload: Record<string, unknown>): void {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.log(`[web-vitals] ${name}`, payload);
    ship({ name, ...payload });
    return;
  }
  // eslint-disable-next-line no-console
  console.log(JSON.stringify({ kind: "web-vital", name, ...payload }));
  ship({ name, ...payload });
}

function readPageTimings(): PageTimings | null {
  const el = document.getElementById("__tc_page_timings__");
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent) as PageTimings;
  } catch {
    return null;
  }
}

function reportLoad(): void {
  const entries = performance.getEntriesByType(
    "navigation",
  ) as PerformanceNavigationTiming[];
  const nav = entries[0];
  if (!nav) return;
  // `loadEventEnd - startTime` = TOTAL page load time. Wall clock from
  // the browser starting the navigation to every image/script/stylesheet
  // being on-screen and the document `load` event firing. This is the
  // canonical "how long did the page take to load" number.
  //
  // `domContentLoadedEventEnd - startTime` = when the HTML was parsed
  // and synchronous scripts ran (usually ~3-10× faster than full load).
  // Closer to "when the page became interactive."
  //
  // `responseEnd - startTime` = TTFB + HTML transfer (just the document,
  // before any sub-resources load).
  const total = Math.round(nav.loadEventEnd - nav.startTime);
  const dcl = Math.round(nav.domContentLoadedEventEnd - nav.startTime);
  const ttfb = Math.round(nav.responseEnd - nav.startTime);
  emit("TOTAL_LOAD", {
    value: total,
    domContentLoaded: dcl,
    documentResponseEnd: ttfb,
    desc: "total page load time in ms (navigationStart → load event)",
  });
}

function reportServerTimings(): void {
  const t = readPageTimings();
  if (!t) return;
  emit("SERVER", {
    agency: t.agency,
    experience: t.experience,
    availability: t.availability,
    render: t.render,
    slug: t.slug,
  });
}

export function WebVitalsReporter() {
  useReportWebVitals((metric) => {
    emit(metric.name, {
      value: Math.round(metric.value),
      rating: metric.rating,
      id: metric.id,
      navigationType: metric.navigationType,
    });
  });

  useEffect(() => {
    // Server-render timings are in the initial HTML — read them as soon
    // as the client mounts. No need to wait for `load`.
    reportServerTimings();

    // `load` event: fires once. If already loaded by the time this effect
    // runs (e.g. during a slow hydration), report immediately; otherwise
    // listen.
    if (document.readyState === "complete") {
      reportLoad();
      return;
    }
    const onLoad = () => reportLoad();
    window.addEventListener("load", onLoad, { once: true });
    return () => window.removeEventListener("load", onLoad);
  }, []);

  return null;
}
